import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { Loader2, RotateCcw, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { tamanoImagenGirada, type Tamano } from '@/lib/ajusteImagenGirada';
import { useTipoArchivoVisor } from '@/hooks/useTipoArchivoVisor';
import { useUrlFirmada } from '@/hooks/useUrlFirmada';

/**
 * Zoom SOLO sobre el archivo, sin que el navegador haga zoom a toda la página.
 *
 * - `ZoomImagen`: rueda (con o sin Ctrl), pellizco de trackpad (Chrome/Edge lo mandan como
 *   `wheel` + `ctrlKey`, Safari como `gesture*`) y pellizco táctil, centrado en el puntero;
 *   arrastrar para mover, doble clic alterna Ajustar/200 %, teclas + - 0 con foco en el visor.
 *   Sin barra de botones de zoom (solo gestos, como el visor nativo del PDF); girar sí la lleva.
 *   Los listeners van con `{ passive: false }` y `preventDefault()` solo dentro del contenedor.
 *   El porcentaje es relativo a "Ajustar" (100 % = la imagen entera cabe en el panel).
 * - `ZoomPdf`: el PDF va en el visor nativo del navegador, con su propia barra de zoom. Sin
 *   botones propios encima.
 * - `VisorArchivo`: elige entre los dos según el tipo real del archivo. Es lo que deben usar las
 *   pantallas de evidencia: una imagen dentro de un `<iframe>` no tiene zoom propio y el
 *   pellizco/Ctrl+rueda agrandaba toda la página.
 */

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 8;
const PASO = 1.25;

const limitar = (s: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, s));

type Vista = { s: number; x: number; y: number; animar: boolean };
const AJUSTADA: Vista = { s: 1, x: 0, y: 0, animar: true };

const BARRA_CLS =
  'absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-0.5 rounded-lg border border-border bg-background/90 p-0.5 text-foreground shadow-sm backdrop-blur';

export interface ZoomImagenProps {
  src: string;
  alt?: string;
  /** Muestra girar izquierda/derecha (90° por clic). Solo visual: no cambia el archivo. */
  girable?: boolean;
  className?: string;
}

export function ZoomImagen({ src: srcGuardada, alt = 'Imagen', girable = false, className }: ZoomImagenProps) {
  // `src` puede ser la URL pública guardada: se pinta con URL firmada (null mientras firma).
  const { url: src } = useUrlFirmada(srcGuardada);
  const cajaRef = useRef<HTMLDivElement>(null);
  const [caja, setCaja] = useState<Tamano>({ w: 0, h: 0 });
  const [natural, setNatural] = useState<Tamano>({ w: 0, h: 0 });
  const [giro, setGiro] = useState(0);
  const [vista, setVistaState] = useState<Vista>(AJUSTADA);
  // Los listeners nativos leen la vista vigente por ref (sin re-suscribirse en cada cambio).
  const vistaRef = useRef(vista);
  const setVista = useCallback((v: Vista) => {
    vistaRef.current = v;
    setVistaState(v);
  }, []);

  useEffect(() => {
    setGiro(0);
    setNatural({ w: 0, h: 0 });
    setVista(AJUSTADA);
  }, [src, setVista]);

  useEffect(() => {
    const el = cajaRef.current;
    if (!el) return;
    const medir = () => setCaja({ w: el.clientWidth, h: el.clientHeight });
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** Punto de pantalla relativo al centro del panel. */
  const punto = useCallback((clientX: number, clientY: number): [number, number] => {
    const r = cajaRef.current?.getBoundingClientRect();
    if (!r) return [0, 0];
    return [clientX - r.left - r.width / 2, clientY - r.top - r.height / 2];
  }, []);

  /** Lleva la escala a `s` dejando fijo el punto (px, py) bajo el puntero. */
  const zoomEn = useCallback(
    (s: number, px = 0, py = 0, animar = false) => {
      const v = vistaRef.current;
      const ns = limitar(s);
      if (ns === v.s) return;
      const k = ns / v.s;
      setVista({ s: ns, x: px - (px - v.x) * k, y: py - (py - v.y) * k, animar });
    },
    [setVista],
  );

  // Rueda y gestos de Safari: nativos con passive:false para poder cancelar el zoom de página.
  useEffect(() => {
    const el = cajaRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const unidad = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
      // El pellizco (ctrlKey) manda deltas chicos y seguidos; la rueda, saltos de ~100 que se
      // topan a 25 para que cada muesca sea ~16 % (sin Ctrl) o ~28 % (con Ctrl).
      const delta = Math.max(-25, Math.min(25, e.deltaY * unidad));
      const k = e.ctrlKey ? 0.01 : 0.006;
      const [px, py] = punto(e.clientX, e.clientY);
      zoomEn(vistaRef.current.s * Math.exp(-delta * k), px, py);
    };
    let base = 1;
    type Gesto = Event & { scale?: number; clientX?: number; clientY?: number };
    const onGestureStart = (e: Event) => {
      e.preventDefault();
      base = vistaRef.current.s;
    };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const g = e as Gesto;
      const r = el.getBoundingClientRect();
      const [px, py] = punto(g.clientX ?? r.left + r.width / 2, g.clientY ?? r.top + r.height / 2);
      zoomEn(base * (g.scale ?? 1), px, py);
    };
    const onGestureEnd = (e: Event) => e.preventDefault();
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('gesturestart', onGestureStart, { passive: false } as AddEventListenerOptions);
    el.addEventListener('gesturechange', onGestureChange, { passive: false } as AddEventListenerOptions);
    el.addEventListener('gestureend', onGestureEnd, { passive: false } as AddEventListenerOptions);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('gesturestart', onGestureStart);
      el.removeEventListener('gesturechange', onGestureChange);
      el.removeEventListener('gestureend', onGestureEnd);
    };
  }, [punto, zoomEn]);

  // Arrastre (un puntero) y pellizco táctil (dos punteros).
  const punteros = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; s: number } | null>(null);
  const distancia = () => {
    const [a, b] = [...punteros.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y) || 1;
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (punteros.current.size === 2) pinch.current = { dist: distancia(), s: vistaRef.current.s };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const prev = punteros.current.get(e.pointerId);
    if (!prev) return;
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (punteros.current.size >= 2 && pinch.current) {
      const [a, b] = [...punteros.current.values()];
      const [px, py] = punto((a.x + b.x) / 2, (a.y + b.y) / 2);
      zoomEn(pinch.current.s * (distancia() / pinch.current.dist), px, py);
      return;
    }
    const v = vistaRef.current;
    if (punteros.current.size === 1 && v.s > 1) {
      setVista({ ...v, x: v.x + e.clientX - prev.x, y: v.y + e.clientY - prev.y, animar: false });
    }
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    punteros.current.delete(e.pointerId);
    if (punteros.current.size < 2) pinch.current = null;
  };

  const onDoubleClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (vistaRef.current.s !== 1) setVista(AJUSTADA);
    else {
      const [px, py] = punto(e.clientX, e.clientY);
      zoomEn(2, px, py, true);
    }
  };

  const acercar = () => zoomEn(vistaRef.current.s * PASO, 0, 0, true);
  const alejar = () => zoomEn(vistaRef.current.s / PASO, 0, 0, true);
  const ajustar = () => setVista(AJUSTADA);
  const girar = (delta: number) => {
    setGiro((g) => g + delta);
    setVista({ s: vistaRef.current.s, x: 0, y: 0, animar: true });
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === '+' || e.key === '=') acercar();
    else if (e.key === '-' || e.key === '_') alejar();
    else if (e.key === '0') ajustar();
    else return;
    e.preventDefault();
  };

  const tam = tamanoImagenGirada(natural, caja, giro);

  return (
    <div
      ref={cajaRef}
      tabIndex={0}
      data-visor-zoom=""
      role="group"
      aria-label={`${alt}: rueda o pellizco para acercar, arrastra para mover, teclas + - 0`}
      onKeyDown={onKeyDown}
      className={cn(
        'relative h-full w-full min-h-0 overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        className,
      )}
    >
      <div
        className={cn('absolute inset-0 touch-none select-none', vista.s > 1 && 'cursor-grab active:cursor-grabbing')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
      >
        <img
          src={src ?? undefined}
          alt={alt}
          draggable={false}
          data-zoom-escala={vista.s}
          onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          className="pointer-events-none absolute left-1/2 top-1/2 max-w-none [image-orientation:from-image]"
          style={{
            // Hasta medir la foto se muestra contenida, sin girar.
            width: tam ? tam.w : undefined,
            height: tam ? tam.h : undefined,
            maxWidth: tam ? 'none' : '100%',
            maxHeight: tam ? 'none' : '100%',
            transform: `translate(-50%, -50%) translate(${vista.x}px, ${vista.y}px) scale(${vista.s}) rotate(${giro}deg)`,
            transition: vista.animar ? 'transform 150ms ease-out' : 'none',
          }}
        />
      </div>

      {/* Sin barra de zoom: solo gestos (rueda o pellizco, arrastrar, doble clic), como el visor
          nativo de los PDF. Girar sí lleva botones porque no tiene gesto. */}
      {girable && (
        <div className={BARRA_CLS}>
          <Button type="button" variant="ghost" size="icon-compact" aria-label="Girar a la izquierda" title="Girar a la izquierda" onClick={() => girar(-90)}>
            <RotateCcw />
          </Button>
          <Button type="button" variant="ghost" size="icon-compact" aria-label="Girar a la derecha" title="Girar a la derecha" onClick={() => girar(90)}>
            <RotateCw />
          </Button>
        </div>
      )}
    </div>
  );
}

export interface ZoomPdfProps {
  src: string;
  title: string;
  className?: string;
  loading?: 'lazy' | 'eager';
}

export function ZoomPdf({ src: srcGuardada, title, className, loading }: ZoomPdfProps) {
  // El fragmento (`#page=N`) se conserva: `useUrlFirmada` lo firma aparte y lo repone.
  const { url: src } = useUrlFirmada(srcGuardada);
  // Solo el visor nativo del navegador: ya trae su barra con - / + / página / buscar. Una barra
  // propia encima duplicaba los botones de aumento.
  return (
    <div className={cn('relative h-full w-full min-h-0', className)}>
      {src && <iframe src={src} title={title} loading={loading} className="h-full w-full border-0" />}
    </div>
  );
}

export interface VisorArchivoProps {
  src: string;
  title: string;
  /** Nombre original del archivo: desempata cuando la URL no trae extensión. */
  nombre?: string | null;
  /** Solo imágenes: girar 90° por clic. */
  girable?: boolean;
  className?: string;
  loading?: 'lazy' | 'eager';
}

/**
 * Comprobante, CEP o adjunto en el visor que le toca: imagen → `ZoomImagen` (zoom solo sobre la
 * foto), PDF u otro → `ZoomPdf` (visor nativo). Si la URL no dice el tipo, lo pregunta al
 * servidor antes de pintar, para no meter una foto en un iframe.
 */
export function VisorArchivo({ src, title, nombre, girable = false, className, loading }: VisorArchivoProps) {
  const tipo = useTipoArchivoVisor(src, nombre);
  if (tipo === null) {
    return (
      <div className={cn('flex h-full w-full min-h-0 items-center justify-center text-muted-foreground', className)}>
        <Loader2 className="size-5 animate-spin" aria-label="Cargando archivo" />
      </div>
    );
  }
  if (tipo === 'imagen') return <ZoomImagen src={src} alt={title} girable={girable} className={className} />;
  return <ZoomPdf key={src} src={src} title={title} className={className} loading={loading} />;
}
